import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SearchInput from '../ui/SearchInput'
import EmptyState from '../ui/EmptyState'
import QuestionItem from './QuestionItem'
import { SECTION_EMPTY_MESSAGE, VIEW_MODES } from './constants'

const TOGGLE_ALL_CLASS = 'tw:bg-transparent tw:border-0 tw:cursor-pointer '
  + 'tw:text-sm tw:text-gray-800 tw:flex tw:items-center tw:gap-1 '
  + 'tw:whitespace-nowrap'

const matchesSearch = (question, search) => {
  if (!search)
    return true
  return question.title.toLowerCase().includes(search.toLowerCase())
}

const compareQuestions = (left, right) => {
  // Grade questions first, then by responses_count desc.
  const leftIsGrade = left.context === 'grade' ? 1 : 0
  const rightIsGrade = right.context === 'grade' ? 1 : 0
  if (leftIsGrade !== rightIsGrade)
    return rightIsGrade - leftIsGrade
  return (right.responses_count || 0) - (left.responses_count || 0)
}

const QuestionList = ({ questions, viewMode, gradeMax }) => {
  const [search, setSearch] = useState('')
  const initialOpen = useMemo(() => new Set(questions.map(question => question.id)), [questions])
  const [openIds, setOpenIds] = useState(initialOpen)

  const filtered = useMemo(() => {
    const sorted = [...questions].sort(compareQuestions)
    return sorted.filter(question => matchesSearch(question, search))
  }, [questions, search])
  const allOpen = openIds.size === filtered.length && filtered.length > 0

  const handleToggle = useCallback(id => {
    setOpenIds(previous => {
      const next = new Set(previous)
      if (next.has(id))
        next.delete(id)
      else
        next.add(id)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setOpenIds(allOpen ? new Set() : new Set(filtered.map(question => question.id)))
  }, [allOpen, filtered])

  const handleSearch = useCallback(event => setSearch(event.target.value), [])

  const emptyMessage = filtered.length === 0 ? <EmptyState message={SECTION_EMPTY_MESSAGE} /> : null

  return (
    <>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <SearchInput
          className="tw:flex-1 tw:max-w-[341px]"
          name="question-search"
          placeholder="Rechercher..."
          value={search}
          onChange={handleSearch}
        />
        <button
          className={TOGGLE_ALL_CLASS}
          type="button"
          onClick={handleToggleAll}
        >
          <span className="tw:underline">{allOpen ? 'Tout fermer' : 'Tout ouvrir'}</span>
          <i
            aria-hidden="true"
            className={`fas ${allOpen ? 'fa-chevron-up' : 'fa-chevron-down'} tw:text-base`}
          />
        </button>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-6">
        {filtered.map(question => (
          <QuestionItem
            key={question.id}
            gradeMax={gradeMax}
            isOpen={openIds.has(question.id)}
            question={question}
            viewMode={viewMode}
            onToggle={handleToggle}
          />
        ))}
        {emptyMessage}
      </div>
    </>
  )
}

QuestionList.propTypes = {
  gradeMax: PropTypes.number.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    context: PropTypes.string.isRequired
  })).isRequired,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired
}

export default QuestionList
